import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Test the name tokenization on a single sanction document
 * @param {string} documentId - ID of the document to test
 * @returns {Promise<Object>} - Results of the test including before & after data
 */
export const testNameTokenization = async (documentId) => {
  try {
    const functions = getFunctions();
    const testAddNameTokensFunc = httpsCallable(functions, 'testAddNameTokens');
    
    console.log(`Testing name tokenization for document: ${documentId}`);
    const result = await testAddNameTokensFunc({ documentId });
    
    return result.data;
  } catch (error) {
    console.error('Error testing name tokenization:', error);
    throw error;
  }
};

/**
 * Run the tokenization process on a batch of documents
 * @param {number} batchSize - Number of documents to process per batch (default 100)
 * @param {string|null} startAfter - Document ID to start after (for pagination)
 * @returns {Promise<Object>} - Results of the batch process
 */
export const runNameTokenizationBatch = async (batchSize = 100, startAfter = null) => {
  try {
    const functions = getFunctions();
    const addNameTokensFunc = httpsCallable(functions, 'addNameTokensToAllDocuments');
    
    console.log(`Running name tokenization batch (size: ${batchSize}, startAfter: ${startAfter || 'beginning'})`);
    const result = await addNameTokensFunc({ batchSize, startAfter });
    
    return result.data;
  } catch (error) {
    console.error('Error running name tokenization batch:', error);
    throw error;
  }
};

/**
 * Process all documents in batches with progress updates
 * @param {number} batchSize - Number of documents per batch
 * @param {Function} progressCallback - Function to call with progress updates
 * @returns {Promise<Object>} - Final results
 */
export const processAllDocuments = async (batchSize = 100, progressCallback = null) => {
  try {
    let isComplete = false;
    let startAfter = null;
    let totalResults = {
      totalProcessed: 0,
      successful: 0,
      failed: 0,
      errors: []
    };
    
    // Process batches until complete
    while (!isComplete) {
      const batchResult = await runNameTokenizationBatch(batchSize, startAfter);
      
      // Update running totals
      totalResults.totalProcessed += batchResult.totalProcessed;
      totalResults.successful += batchResult.successful;
      totalResults.failed += batchResult.failed;
      
      if (batchResult.errors && batchResult.errors.length > 0) {
        totalResults.errors = [...totalResults.errors, ...batchResult.errors];
      }
      
      // Call progress callback if provided
      if (progressCallback) {
        progressCallback({
          ...totalResults,
          currentBatch: batchResult,
          isComplete: batchResult.isComplete
        });
      }
      
      // Check if we're done or set up next batch
      if (batchResult.isComplete) {
        isComplete = true;
      } else {
        startAfter = batchResult.lastDocumentId;
      }
    }
    
    return totalResults;
  } catch (error) {
    console.error('Error processing all documents:', error);
    throw error;
  }
};

/**
 * Check the effectiveness of tokenization by running test searches
 * @param {Array<string>} testQueries - List of test search strings to try
 * @returns {Promise<Object>} - Results of test searches
 */
export const testSearchEffectiveness = async (testQueries = []) => {
  // This would be implemented once you update your search algorithm
  // to use the nameTokensSet field
  console.warn('Search effectiveness testing not yet implemented');
  return {
    message: 'This feature will be available after updating the search algorithm to use nameTokensSet'
  };
}; 